import React from 'react';
import { LabelService, type ButtonAction } from '../../services/label.service';
import * as styles from './UnifiedButton.css';
import {
  MdSave, MdAdd, MdEdit, MdCheck, MdCheckCircle, MdDelete, MdRemove,
  MdClose, MdArrowBack, MdArrowForward, MdContentCopy, MdVisibility,
  MdVisibilityOff, MdSync, MdSwapVert, MdCheckBox, MdCheckBoxOutlineBlank,
  MdFilterList, MdSearch, MdRestartAlt, MdCleaningServices, MdRefresh,
  MdFileUpload, MdFileDownload, MdUpload, MdDownload
} from 'react-icons/md';

// Mapping centralisé action -> icône Material (Google). Remplace les anciens emojis.
const ACTION_ICONS: Partial<Record<ButtonAction, React.ReactNode>> = {
  save: <MdSave />,
  create: <MdAdd />,
  update: <MdEdit />,
  submit: <MdCheck />,
  confirm: <MdCheck />,
  delete: <MdDelete />,
  remove: <MdRemove />,
  cancel: <MdClose />,
  close: <MdClose />,
  back: <MdArrowBack />,
  next: <MdArrowForward />,
  previous: <MdArrowBack />,
  add: <MdAdd />,
  edit: <MdEdit />,
  duplicate: <MdContentCopy />,
  view: <MdVisibility />,
  export: <MdFileUpload />,
  import: <MdFileDownload />,
  upload: <MdUpload />,
  download: <MdDownload />,
  show: <MdVisibility />,
  hide: <MdVisibilityOff />,
  toggle: <MdSync />,
  reorder: <MdSwapVert />,
  select: <MdCheckBox />,
  deselect: <MdCheckBoxOutlineBlank />,
  filter: <MdFilterList />,
  search: <MdSearch />,
  validate: <MdCheckCircle />,
  reset: <MdRestartAlt />,
  clear: <MdCleaningServices />,
  refresh: <MdRefresh />,
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  action?: ButtonAction;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info';
  isLoading?: boolean;
  entityName?: string;
  count?: number;
  icon?: string | boolean | React.ReactNode; // true = icône par défaut de l'action, ReactNode = icône personnalisée
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  iconOnly?: boolean; // affiche uniquement l'icône
  autoCollapse?: boolean; // masque le texte si le conteneur est trop étroit
  allowWrap?: boolean; // autorise le retour à la ligne du texte
  children?: React.ReactNode;
}

export function UnifiedButton({
  action,
  variant,
  isLoading = false,
  entityName,
  count,
  icon,
  size = 'medium',
  fullWidth = false,
  iconOnly = false,
  autoCollapse = true,
  allowWrap = false,
  className = '',
  children,
  disabled,
  ...props
}: ButtonProps) {

  const getButtonContent = () => {
    // Détermine quelle icône afficher
    const displayIcon = (() => {
      if (icon === true && action) return ACTION_ICONS[action] ?? null;
      if (icon === false) return null;
      if (React.isValidElement(icon)) return icon;
      if (typeof icon === 'string') return icon;
      return null;
    })();

    // Si des enfants sont fournis, on utilise l'icône + le texte fourni
    if (children) {
      return (
        <>
          {displayIcon && <span className={styles.buttonIcon}>{displayIcon}</span>}
          {!iconOnly && <span className={styles.buttonText}>{children}</span>}
        </>
      );
    }

    // Sinon, on utilise le système d'actions centralisé
    if (action) {
      const label = LabelService.getButtonLabel(action, {
        entityName,
        isLoading,
        count
      });

      return (
        <>
          {displayIcon && <span className={styles.buttonIcon}>{displayIcon}</span>}
          {!iconOnly && <span className={styles.buttonText}>{label}</span>}
        </>
      );
    }

    return 'Bouton';
  };

  const getVariant = (): 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' => {
    if (variant) {
      return variant;
    }
    // Variantes par défaut selon l'action
    switch (action) {
      case 'delete': return 'danger';
      case 'save':
      case 'submit': return 'primary';
      case 'cancel':
      case 'close': return 'secondary';
      case 'create':
      case 'add': return 'success';
      case 'edit': return 'info';
      default: return 'primary';
    }
  };

  const buttonVariant = getVariant();
  const loadingClass = isLoading ? 'button-loading' : '';
  const disabledClass = (disabled || isLoading) ? 'button-disabled' : '';

  const classes = [
    styles.unifiedButton,
    styles.buttonSizes[size],
    (!iconOnly && !autoCollapse && !allowWrap) ? styles.buttonHeights[size] : '',
    styles.buttonVariants[buttonVariant],
    fullWidth ? styles.buttonFullWidth : '',
    loadingClass,
    disabledClass,
    autoCollapse ? 'button-responsive' : '',
    iconOnly ? styles.buttonIconOnly : '',
    allowWrap ? styles.allowWrap : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      className={classes}
      disabled={disabled || isLoading}
      {...props}
    >
      {getButtonContent()}
    </button>
  );
}

// Boutons spécialisés préconfigurés sans style inline
export function SaveButton({ className = '', ...props }: Omit<ButtonProps, 'action'>) {
  return (
    <UnifiedButton 
      action="save" 
      icon={true} 
      autoCollapse={false}
      className={`${styles.formButtons.save} ${className}`}
      {...props}
    >
      {props.children || 'Enregistrer'}
    </UnifiedButton>
  );
}

export function CancelButton({ className = '', ...props }: Omit<ButtonProps, 'action'>) {
  return (
    <UnifiedButton 
      action="cancel" 
      variant="secondary" 
      icon={true} 
      autoCollapse={false}
      className={`${styles.formButtons.cancel} ${className}`}
      {...props}
    >
      {props.children || 'Annuler'}
    </UnifiedButton>
  );
}

export function DeleteButton(props: Omit<ButtonProps, 'action'>) {
  return <UnifiedButton action="delete" variant="danger" icon={true} {...props} />;
}

export function EditButton(props: Omit<ButtonProps, 'action'>) {
  return <UnifiedButton action="edit" variant="info" icon={true} {...props} />;
}

export function CreateButton(props: Omit<ButtonProps, 'action'>) {
  return <UnifiedButton action="create" variant="success" icon={true} {...props} />;
}

export function AddButton({ className = '', ...props }: Omit<ButtonProps, 'action'>) {
  return (
    <UnifiedButton 
      action="add" 
      variant="primary" 
      icon={true} 
      autoCollapse={false} 
      className={`${styles.formButtons.add} ${className}`}
      {...props}
    >
      {props.children || 'Ajouter'}
    </UnifiedButton>
  );
}

export function CloseButton(props: Omit<ButtonProps, 'action'>) {
  return (
    <UnifiedButton
      action="close"
      variant="secondary"
      icon={<MdClose />}
      iconOnly={true}
      aria-label={(props as any)['aria-label'] || 'Fermer'}
      title={props.title || 'Fermer'}
      {...props}
    />
  );
}

export function SubmitButton(props: Omit<ButtonProps, 'action'>) {
  return <UnifiedButton action="submit" variant="primary" icon={true} {...props} />;
}

export function DuplicateButton(props: Omit<ButtonProps, 'action'>) {
  return <UnifiedButton action="duplicate" variant="info" icon={true} {...props} />;
}

export function ViewButton(props: Omit<ButtonProps, 'action'>) {
  return <UnifiedButton action="view" variant="secondary" icon={true} {...props} />;
}

export function RefreshButton({ className = '', ...props }: Omit<ButtonProps, 'action'>) {
  return (
    <UnifiedButton 
      action="refresh" 
      variant="secondary" 
      icon={true} 
      autoCollapse={false} 
      className={`${styles.formButtons.refresh} ${className}`}
      {...props}
    >
      {props.children || 'Actualiser'}
    </UnifiedButton>
  );
}

export function FilterButton(props: Omit<ButtonProps, 'action'>) {
  return <UnifiedButton action="filter" variant="secondary" icon={true} {...props} />;
}

export function ClearButton(props: Omit<ButtonProps, 'action'>) {
  return <UnifiedButton action="clear" variant="secondary" icon={true} {...props} />;
}

export function ReorderButton({ className = '', ...props }: Omit<ButtonProps, 'action'>) {
  return (
    <UnifiedButton 
      action="reorder" 
      variant="secondary" 
      icon={true} 
      autoCollapse={false} 
      className={`${styles.formButtons.reorder} ${className}`}
      {...props}
    >
      {props.children || 'Réorganiser'}
    </UnifiedButton>
  );
}


export function ToggleVisibilityButton({
  isVisible,
  onClick,
  isLoading = false,
  className = '',
  iconOnly = false,
  size = 'small'
}: {
  isVisible: boolean;
  onClick: () => void;
  isLoading?: boolean;
  className?: string;
  iconOnly?: boolean;
  size?: 'small' | 'medium' | 'large';
}) {
  const label = isVisible ? 'Masquer' : 'Afficher';
  const icon = isVisible ? <MdVisibility /> : <MdVisibilityOff />; // Œil ouvert si visible, barré si masqué
  const classes = [
    styles.unifiedButton,
    'button-responsive',
    iconOnly ? styles.buttonIconOnly : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className={classes}
      title={label}
      aria-label={iconOnly ? label : undefined}
    >
      <span className={styles.buttonIcon}>{icon}</span>
      {!iconOnly && <span className={styles.buttonText}>{label}</span>}
    </button>
  );
}
